import {
    List,
    Datagrid,
    TextField,
    ImageField,
    TopToolbar,
    useRecordContext,
    RichTextField,
    Filter,
    SelectInput,
    TextInput,
    useRefresh,
    useDataProvider,
    DataProviderContext,
} from 'react-admin';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import { useNavigate } from 'react-router';
import { Add } from '@mui/icons-material';
import Button from '@mui/material/Button';
import { Edit } from 'lucide-react';
import { CopyIdButton } from '../../common/CopyIdButton';
import useIsPermission from '../../hooks/useIsPermission';
import { useState, useMemo } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import QuestionShowModal from './QuestionShowModal';
import { QUESTION_TYPES } from '../../common/constants';
import { createCustomGoDataProvider } from '../../services';
import DeleteQuestionsButton from './DeleteQuestionButton';
const Actions = () => {
    const navigate = useNavigate();
    return (
        <div style={{ display: 'flex' }}>
            <Button
                startIcon={<Add />}
                onClick={() => {
                    navigate(`/questions_bank/create`);
                }}
                sx={{ padding: '0 5px' }}
            >
                Create
            </Button>
        </div>
    );
};

const CustomListActions = () => (
    <TopToolbar>
        <Actions />
    </TopToolbar>
);

const EditButton = () => {
    const navigate = useNavigate();
    const record = useRecordContext();
    return (
        <Button
            startIcon={<Edit />}
            onClick={e => {
                navigate(`/questions_bank/${record.id}/edit`);
            }}
        >
            Edit
        </Button>
    );
};
const ShowButton = ({ onShow }: { onShow: (record: any) => void }) => {
    const record = useRecordContext();

    return (
        <Button startIcon={<VisibilityIcon />} onClick={() => onShow(record)}>
            Show
        </Button>
    );
};

const ListFilter = (props: any) => (
    <Filter {...props}>
        <TextInput label="Search by Name" source="name" alwaysOn />
        <TextInput label="Search by Content" source="content" alwaysOn />
        <SelectInput source="type" label={'Type'} choices={QUESTION_TYPES} alwaysOn />
    </Filter>
);

const QuestionRowActions = ({ setOpen, open, setSelectedRecord }: any) => {
    const refresh = useRefresh();
    const record = useRecordContext() as any;
    return (
        <div className="flex gap-2">
            <ShowButton
                onShow={(record: any) => {
                    setSelectedRecord(record);
                    setOpen(true);
                }}
            />
            <DeleteQuestionsButton
                questionIds={[record?.id]}
                onSuccess={() => {
                    refresh();
                }}
            />
        </div>
    );
};
const CustomBulkActionButtons = ({
    selectedIds = [],
    setSelectedIds,
}: {
    selectedIds?: string[];
    setSelectedIds?: any;
}) => {
    return (
        <DeleteQuestionsButton
            questionIds={selectedIds}
            setSelectedIds={setSelectedIds}
            onSuccess={() => {}}
        />
    );
};

const QuestionBankList = () => {
    const [open, setOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<any>(null);
    const { isDeletable, isWritable, isUpdatable } = useIsPermission('QUESTION_BANK');
    const baseDataProvider = useDataProvider();

    const customDataProvider = useMemo(
        () =>
            createCustomGoDataProvider(baseDataProvider, [
                { resource: 'questions', apiEndpoint: '/qb/questions' },
            ]),
        [baseDataProvider],
    );

    return (
        <>
            <DataProviderContext.Provider value={customDataProvider}>
                <List
                    resource="questions"
                    filters={<ListFilter />}
                    actions={<CustomListActions />}
                    queryOptions={{
                        refetchOnWindowFocus: false,
                        refetchOnReconnect: false,
                    }}
                >
                    <Datagrid bulkActionButtons={<CustomBulkActionButtons />}>
                        <CopyIdButton />
                        <TextField source="name" />
                        <TextField source="type" />
                        <RichTextField source="content" />
                        <ImageField source="image" />
                        <FormattedDateTimeField source="createdAt" />
                        {isUpdatable && <EditButton />}
                        <QuestionRowActions
                            setOpen={setOpen}
                            open={open}
                            setSelectedRecord={setSelectedRecord}
                        />
                    </Datagrid>
                </List>
            </DataProviderContext.Provider>

            <QuestionShowModal
                open={open}
                onClose={() => setOpen(false)}
                record={selectedRecord}
                title=" Preview"
            />
        </>
    );
};
export default QuestionBankList;
